import { createReport } from "docx-templates/lib/browser.js";
import { cdate } from "./CDate";
import { buildChartJsConfig, generateStaticChartImage } from "../components/chart/DynamicChart";
import { base64Decode, getPNGDimensions, jpegProps } from "./printingUtilities";
import { distinct, chunkArray } from "./arrayUtilities";

export const generateDocx = async (clientConfig, network, definition) => {
    const commonBuilders = {
        ...commonDocxBuilders(clientConfig),
        ...chartDocxBuilders(),
        ...definition?.additionalJsContext
    }
    const doc = await createReport({ maximumWalkingDepth: Infinity, ...definition, additionalJsContext: commonBuilders });
    if (!clientConfig.docx2pdf?.enable) {
        return { data: doc, mime: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', type: 'docx' };
    }
    const formData = new FormData();
    formData.append('file', new Blob([doc], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' }));
    try {
        const result = await network.fetchData('/api/v1/docx2pdf', { method: 'POST', body: formData });
        const pdf = await result.arrayBuffer();
        return { data: pdf, mime: 'application/pdf', type: 'pdf' };
    } catch (err) {
        console.log(err);
        return { data: doc, mime: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', type: 'docx' };
    }
}

export function commonDocxBuilders(configuration) {
    return {
        distinct,
        formatDateTime: function (date, showTZ = true) {
            return date && date !== " " ? cdate(date, configuration.tz).dateTimeFormat(configuration.timezones.enable && showTZ): date;
        },
        formatDate: function (date, showTZ = true) {
            return date && date !== " " ? cdate(date, configuration.tz).dateFormat(configuration.timezones.enable && showTZ): date;
        },
        formatTime: function (date, showTZ = true) {
            return date && date !== " " ? cdate(date, configuration.tz).timeFormat(configuration.timezones.enable && showTZ): date;
        },
        customDateFormat: function (date, format, showTZ = true) {
            return date && date !== " " ? cdate(date, configuration.tz).customFormat(format, configuration.timezones.enable && showTZ): date;
        },
        now: function () {
            return new Date();
        },
        joinArray: function (data) {
            return data && Array.isArray(data) ? data.join(", ") : data;
        },
        chunkArray: function (data, chunkSize) {
            return data && Array.isArray(data) ? chunkArray(data, chunkSize) : data;
        },
        makeImage: function (...props) {
            let w = -1, h = -1, extension = null, width, height;
            let data;
            if (props.length === 3) {
                data = props[0];
                width = props[1];
                height = props[2];
            } else {
                data = props[1];
                if (data && data.startsWith("data:image/png")) {
                    data = data.slice(data.indexOf(",") + 1);
                    const dims = getPNGDimensions(base64Decode(data));
                    w = dims.width;
                    h = dims.height;
                    extension = ".png";
                } else if (data && (data.startsWith("data:image/jpg") || data.startsWith("data:image/jpeg"))) {
                    data = data.slice(data.indexOf(",") + 1);
                    const props = jpegProps(base64Decode(data));
                    w = props?.w ?? 350;
                    h = props?.h ?? 350;
                    extension = ".jpg";
                } else {
                    throw new Error("Unknown image format " + (data ? data.slice(0, data.indexOf(",") + 1) : ""));
                }
                const colspan = props[0];
                width = (16 / 12) * colspan;
                height = width * (h / w);
            }
            if (!data || data === " " || data === "data:,") {
                data = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABAAAAABCAQAAAB9cObUAAAADklEQVR42mP8/58BLwAAPwMCAIC4tp0AAAAASUVORK5CYII=";
            }
            return { data, extension, width, height };
        }
    };
}

export function chartDocxBuilders() {
    return {
        makeChart: async function (width, height, type, title, seriesData, labels, seriesLabels, showLegend, natural, yMax, seriesColors, options) {
            const series = seriesData?.map((d, idx) => {
                const label = seriesLabels?.[idx];
                let color;
                if (Array.isArray(seriesColors)) {
                    if (seriesData.length === 1 && Array.isArray(labels) && seriesColors.length === labels.length) {
                        color = seriesColors;
                    } else {
                        color = seriesColors[idx];
                    }
                } else {
                    color = seriesColors;
                }
                return ({ label, data: d, color });
            });
            try {
                return await generateStaticChartImage({ labels, series: series ?? [], title, type, options: { showLegend, natural, yMax, ...(options || {}) } }, seriesData, height, width);
            } catch (err) {
                console.log("Chart generation error:", err);
                return;
            }
        },
        renderChartSpec: async function (width, height, chart, jobs) {
            try {
                return await generateStaticChartImage(chart, jobs, height, width);
            } catch (err) {
                console.log("Chart generation error:", err);
                return;
            }
        },
        chartData: function (chart, jobs) {
            const { data } = buildChartJsConfig(chart, jobs);
            if (chart.series?.length > 0) {
                return data.datasets.reduce((acc, ds) => {
                    ds.data.forEach((v, i) => {
                        acc.push({
                            label: data.labels ? data.labels[i] : v.x,
                            value: typeof v === "object" ? v.y : v
                        });
                    });
                    return acc;
                }, []);
            } else {
                return data.reduce((acc, v) => {
                    acc.push({ label: v.x, value: v.y });
                    return acc;
                }, []);
            }
        }
    };
}

